import { useCallback, useEffect, useRef, useState } from "react";
import GroupingConfirmModal from "./GroupingConfirmModal";
import "./GroupingsView.css";
import "./WordListBox.css";

const VISIBLE_WORDS = 6;
const WORD_ROW_HEIGHT = 30;

export default function GroupingRemoveWordsModal({
  open = false,
  words = [],
  busy = false,
  onConfirm = async () => {},
  onClose = () => {},
}) {
  const listRef = useRef(null);
  const [selected, setSelected] = useState(() => new Set());
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });

  const entries = (Array.isArray(words) ? words : [])
    .map((item) => (typeof item === "string" ? item : item?.mot))
    .map((mot) => String(mot || "").trim())
    .filter(Boolean);

  const updateScrollFades = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      setScrollFades({ top: false, bottom: false });
      return;
    }
    const threshold = 2;
    setScrollFades({
      top: el.scrollTop > threshold,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - threshold,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    const frame = requestAnimationFrame(updateScrollFades);
    return () => cancelAnimationFrame(frame);
  }, [open, words, updateScrollFades]);

  const toggleMot = (mot) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(mot)) next.delete(mot);
      else next.add(mot);
      return next;
    });
  };

  const handleConfirm = () => {
    if (busy || selected.size === 0) return;
    onConfirm([...selected]);
  };

  const selectedCount = selected.size;
  const selectedCountLabel =
    selectedCount <= 1
      ? `${selectedCount} mot à retirer`
      : `${selectedCount} mots à retirer`;

  return (
    <GroupingConfirmModal
      open={open}
      title="Retirer un ou plusieurs mots du groupement"
      confirmLabel="Valider"
      cancelLabel="Annuler"
      busy={busy}
      confirmDisabled={selectedCount === 0}
      className="grouping-confirm-modal--wide"
      actionsStart={
        <p className="grouping-remove-words__count">{selectedCountLabel}</p>
      }
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <p className="grouping-remove-words__intro">
        Cochez un ou plusieurs mots pour les retirer de ce groupement
        thématique. Ils resteront dans le registre.
      </p>
      <div
        className={[
          "grouping-picker-frame grouping-remove-words__frame",
          scrollFades.top ? "grouping-picker-frame--fade-top" : "",
          scrollFades.bottom ? "grouping-picker-frame--fade-bottom" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ height: VISIBLE_WORDS * WORD_ROW_HEIGHT + 2 }}
      >
        <div
          ref={listRef}
          className={`listbox custom-scroll grouping-remove-words__list${
            entries.length > VISIBLE_WORDS ? " grouping-remove-words__list--overflow" : ""
          }`}
          onScroll={updateScrollFades}
          role="listbox"
          aria-multiselectable="true"
          aria-label="Mots du groupement thématique"
        >
          {entries.length === 0 ? (
            <p className="groupings-detail-empty groupings-detail-empty--in-frame">
              Il n&apos;y a actuellement aucun mot affilié à ce groupement
              thématique.
            </p>
          ) : (
            entries.map((mot) => {
              const checked = selected.has(mot);
              return (
                <label
                  key={mot}
                  className={`grouping-remove-words__row${
                    checked ? " grouping-remove-words__row--checked" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    className="grouping-remove-words__input"
                    checked={checked}
                    onChange={() => toggleMot(mot)}
                    disabled={busy}
                  />
                  <span className="grouping-remove-words__marker" aria-hidden="true" />
                  <span className="grouping-remove-words__label">{mot}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </GroupingConfirmModal>
  );
}
